import { useId, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation } from "wouter";
import { Archive, ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { z } from "zod";
import { Alert, Button, ErrorState, PageHeader, Panel, Pill, SelectField, Skeleton, TextField, cx } from "../components/ui.tsx";
import { ConfirmDialog } from "../components/dialog.tsx";
import { useToast } from "../components/toast.tsx";
import { ApiError, applyFieldErrors, get, post, put } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import { useUnsavedGuard } from "../lib/guard.tsx";
import type { AgendaEventDetail, Group } from "../lib/types.ts";
import { agendaCompleteness, COMPLETENESS_LABEL, taskSchema } from "../../shared/agenda.ts";
import { formatInstantBR } from "../../shared/dates.ts";
import { optionalText, requiredText } from "../../shared/setup.ts";
import { LIMITS } from "../../shared/limits.ts";
import { BackLink, OverflowMenu, StickyActions } from "../components/nav-controls.tsx";
import { usePageTitle } from "../lib/nav.tsx";

const formSchema = z.object({
  name: requiredText(LIMITS.name, "Informe o nome da ação."),
  editor_group_id: z.string().min(1, "Selecione o grupo editor."),
  committee_name: optionalText(LIMITS.name),
  event_on: z.string(),
  target_audience: optionalText(LIMITS.actionDescription),
  target_count: z.union([z.number(), z.null()]).refine((v) => v === null || (Number.isInteger(v) && v >= 0), "Informe um número inteiro de participantes (zero ou mais)."),
  tasks: z.array(taskSchema).max(LIMITS.collection),
});
type Form = z.input<typeof formSchema>;

const toForm = (d: AgendaEventDetail | null, defaultGroup: string): Form => ({
  name: d?.name ?? "",
  editor_group_id: d?.editor_group_id ?? defaultGroup,
  committee_name: d?.committee_name ?? "",
  event_on: d?.event_on ?? "",
  target_audience: d?.target_audience ?? "",
  target_count: d?.target_count ?? null,
  tasks: (d?.tasks ?? []).map((t) => ({ id: t.id, description: t.description, starts_on: t.starts_on ?? "", ends_on: t.ends_on ?? "" })),
});

/** T11 route: an existing agenda event or a new one (`nova`). */
export function AgendaEventPage({ id }: { id: string }) {
  const plan = usePlan();
  const isNew = id === "nova";
  const q = useQuery({
    queryKey: planKey(plan.id, "agenda-event", id),
    queryFn: async () => (await get<{ data: AgendaEventDetail }>(`/plans/${plan.id}/agenda/events/${id}`)).data,
    enabled: !isNew,
  });
  const groups = useQuery({ queryKey: planKey(plan.id, "groups"), queryFn: async () => (await get<{ data: Group[] }>(`/plans/${plan.id}/groups`)).data });
  if ((!isNew && q.isPending) || groups.isPending) return <Skeleton lines={8} />;
  if (!isNew && q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404) return <Alert tone="warn" title="Ação da agenda não encontrada">Ela pode ter sido arquivada.</Alert>;
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  if (groups.isError) return <ErrorState error={groups.error} onRetry={() => void groups.refetch()} />;
  const detail = isNew ? null : q.data!;
  return <EventEditor key={detail ? `${detail.id}:${detail.version}` : "new"} detail={detail} groups={groups.data.filter((g) => !g.archived_at)} />;
}

function EventEditor({ detail, groups }: { detail: AgendaEventDetail | null; groups: Group[] }) {
  const plan = usePlan();
  const qc = useQueryClient();
  const toast = useToast();
  const [, navigate] = useLocation();
  const [conflict, setConflict] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const mine = plan.membership.groups.map((g) => g.id);
  // Creation: participants pick one of their groups; afterwards only the instructor transfers (R17).
  const groupChoices = detail
    ? (detail.capabilities.can_change_group ? groups : groups.filter((g) => g.id === detail.editor_group_id))
    : plan.capabilities.can_manage ? groups : groups.filter((g) => mine.includes(g.id));
  const canEdit = detail ? detail.capabilities.can_edit : groupChoices.length > 0;

  const form = useForm<Form>({ resolver: zodResolver(formSchema), defaultValues: toForm(detail, groupChoices[0]?.id ?? ""), mode: "onSubmit", reValidateMode: "onChange" });
  const tasks = useFieldArray({ control: form.control, name: "tasks", keyName: "_key" });
  const values = useWatch({ control: form.control });
  useUnsavedGuard(canEdit && form.formState.isDirty);

  const live = useMemo(() => agendaCompleteness({
    committee_name: values.committee_name?.trim() || null,
    event_on: values.event_on || null,
    target_audience: values.target_audience?.trim() || null,
    target_count: typeof values.target_count === "number" ? values.target_count : null,
    tasks: (values.tasks ?? []).map((t) => ({ description: t?.description ?? null, starts_on: t?.starts_on || null, ends_on: t?.ends_on || null })),
  }), [values]);

  const save = form.handleSubmit(async (v) => {
    setError(null);
    const body = { ...v, event_on: v.event_on || null, tasks: v.tasks.map((t, i) => ({ ...t, id: t.id || null, sort_order: i })) };
    try {
      if (!detail) {
        const res = await post<{ data: AgendaEventDetail }>(`/plans/${plan.id}/agenda/events`, body);
        form.reset(v);
        toast("success", "Ação da agenda salva.");
        await qc.invalidateQueries({ queryKey: planKey(plan.id, "agenda") });
        navigate(`/planos/${plan.id}/agenda/${res.data.id}`, { replace: true });
      } else {
        const res = await put<{ data: AgendaEventDetail }>(`/plans/${plan.id}/agenda/events/${detail.id}`, { ...body, expected_version: detail.version });
        qc.setQueryData(planKey(plan.id, "agenda-event", detail.id), res.data);
        void qc.invalidateQueries({ queryKey: planKey(plan.id, "agenda") });
        toast("success", "Ação da agenda salva.");
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === "VERSION_CONFLICT") setConflict("Esta ação da agenda foi alterada por outra pessoa. Confira a versão atual antes de salvar.");
      else {
        applyFieldErrors(e, form.setError as never);
        const first = e instanceof ApiError ? e.fieldErrors[0]?.path : undefined;
        try {
          if (first) form.setFocus(first as never);
          else window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
        setError(e instanceof Error ? e.message : "Não foi possível salvar.");
      }
    }
  });

  async function archive() {
    try {
      await post(`/plans/${plan.id}/agenda/events/${detail!.id}/archive`, { expected_version: detail!.version });
      form.reset(form.getValues());
      await qc.invalidateQueries({ queryKey: planKey(plan.id, "agenda") });
      toast("success", "Ação da agenda arquivada.");
      navigate(`/planos/${plan.id}/agenda`);
    } catch (e) {
      setArchiving(false);
      toast("danger", e instanceof Error ? e.message : "Não foi possível arquivar.");
    }
  }

  const errors = form.formState.errors;
  usePageTitle(detail?.name ?? "Nova ação da agenda", plan.name);
  return (
    <>
      <PageHeader
        back={<BackLink fallback={`/planos/${plan.id}/agenda`} label="Voltar" />}
        actions={detail?.capabilities.can_archive && (
          <OverflowMenu label="Mais opções da ação da agenda" items={[
            { label: "Arquivar ação da agenda", danger: true, icon: <Archive className="size-4" />, onSelect: () => setArchiving(true) },
          ]} />
        )}
        title={detail?.name ?? "Nova ação da agenda"}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {live.complete ? <Pill>{COMPLETENESS_LABEL.complete}</Pill> : <Pill tone="gold">{COMPLETENESS_LABEL.incomplete}</Pill>}
            {detail?.updated_by_name && <span>Última alteração por {detail.updated_by_name} em {formatInstantBR(detail.updated_at)}</span>}
          </span>
        }
      />
      {conflict && (
        <Alert tone="warn" title="Conflito de edição" action={<Button variant="secondary" onClick={() => { setConflict(null); void qc.invalidateQueries({ queryKey: planKey(plan.id, "agenda-event", detail!.id) }); }}>Carregar versão atual (descarta suas alterações)</Button>}>
          {conflict}
        </Alert>
      )}
      {!canEdit && <Alert tone="info">{detail?.readonly_reason ?? "Você ainda não faz parte de um grupo editor desta oficina."}</Alert>}
      {error && !conflict && <Alert tone="danger">{error}</Alert>}
      <form noValidate onSubmit={save}>
        <fieldset disabled={!canEdit} className="min-w-0">
          <Panel>
            <div className="grid gap-x-5 md:grid-cols-2">
              <TextField label="Nome da ação" required error={errors.name?.message} {...form.register("name")} />
              <SelectField label="Grupo editor" required error={errors.editor_group_id?.message} {...form.register("editor_group_id")}
                hint={detail && !detail.capabilities.can_change_group ? "Somente o instrutor transfere a ação para outro grupo." : undefined}>
                {groupChoices.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </SelectField>
              <TextField label="Comissão ou comitê responsável" error={errors.committee_name?.message} {...form.register("committee_name")} hint="Informação descritiva; não define quem edita." />
              <TextField label="Data do evento" type="date" error={errors.event_on?.message} {...form.register("event_on")} />
              <TextField label="Público-alvo" error={errors.target_audience?.message} {...form.register("target_audience")} />
              <TextField label="Quantidade prevista de participantes" type="number" inputMode="numeric" min={0} step={1} error={errors.target_count?.message}
                {...form.register("target_count", { setValueAs: (v) => (v === "" || v === null ? null : Number(v)) })} />
            </div>
          </Panel>
          <Panel className="mt-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-bold">Tarefas e datas</h2>
              <p className="text-xs text-muted">Cada tarefa tem início e término próprios; podem ocorrer antes ou depois do evento.</p>
            </div>
            <div className="mt-3 hidden grid-cols-[2rem_minmax(0,1fr)_9.5rem_9.5rem_7rem] gap-3 border-b border-line pb-2 text-xs font-bold text-muted md:grid" aria-hidden>
              <span>#</span><span>Tarefa</span><span>Início</span><span>Término</span><span />
            </div>
            {tasks.fields.length === 0 && <p className="py-4 text-sm text-muted">Nenhuma tarefa cadastrada ainda.</p>}
            <ol className="divide-y divide-line">
              {tasks.fields.map((f, i) => {
                const e = errors.tasks?.[i];
                return (
                  <li key={f._key} className="grid gap-x-3 gap-y-1 py-3 md:grid-cols-[2rem_minmax(0,1fr)_9.5rem_9.5rem_7rem] md:items-start">
                    <span className="pt-2 text-sm font-bold text-muted">{i + 1}</span>
                    <Cell label="Tarefa" error={e?.description?.message}>
                      {(id) => <textarea id={id} rows={2} aria-invalid={e?.description ? true : undefined} className={cx(inputCls, "autogrow")} {...form.register(`tasks.${i}.description`)} />}
                    </Cell>
                    <Cell label="Início" error={e?.starts_on?.message}>
                      {(id) => <input id={id} type="date" className={inputCls} aria-invalid={e?.starts_on ? true : undefined} {...form.register(`tasks.${i}.starts_on`)} />}
                    </Cell>
                    <Cell label="Término" error={e?.ends_on?.message}>
                      {(id) => <input id={id} type="date" className={inputCls} aria-invalid={e?.ends_on ? true : undefined} {...form.register(`tasks.${i}.ends_on`)} />}
                    </Cell>
                    {canEdit && (
                      <div className="flex gap-1">
                        <IconBtn label={`Mover tarefa ${i + 1} para cima`} disabled={i === 0} onClick={() => tasks.move(i, i - 1)}><ArrowUp className="size-4" /></IconBtn>
                        <IconBtn label={`Mover tarefa ${i + 1} para baixo`} disabled={i === tasks.fields.length - 1} onClick={() => tasks.move(i, i + 1)}><ArrowDown className="size-4" /></IconBtn>
                        <IconBtn label={`Remover tarefa ${i + 1}`} onClick={() => tasks.remove(i)}><Trash2 className="size-4" /></IconBtn>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
            {canEdit && (
              <Button variant="secondary" className="mt-2" disabled={tasks.fields.length >= LIMITS.collection}
                onClick={() => tasks.append({ id: null, description: "", starts_on: "", ends_on: "" })}>
                <Plus className="size-4" aria-hidden />Adicionar tarefa
              </Button>
            )}
          </Panel>
        </fieldset>
        {canEdit && !live.complete && (
          <Alert tone="info" title="Cadastro em elaboração. Para completar, falta:">
            <ul className="list-disc pl-5">
              {[...new Set(live.pending_fields.map((p) => p.path.startsWith("tasks.") ? `Tarefa ${Number(p.path.split(".")[1]) + 1}: ${p.message}` : p.message))].map((m) => <li key={m}>{m}</li>)}
            </ul>
            <p className="mt-1 text-xs">É possível salvar assim mesmo.</p>
          </Alert>
        )}
        {canEdit && (
          <StickyActions dirty={form.formState.isDirty} onSave={() => void save()}
            summary={<span className="text-muted">{tasks.fields.length} tarefa(s)</span>}>
            <Button type="submit" loading={form.formState.isSubmitting} disabled={!!detail && !form.formState.isDirty}>Salvar ação da agenda</Button>
          </StickyActions>
        )}
      </form>
      <ConfirmDialog open={archiving} onOpenChange={setArchiving} title="Arquivar ação da agenda?" confirmLabel="Arquivar" danger onConfirm={() => void archive()}>
        <p className="text-sm">A ação e suas tarefas deixam de aparecer na agenda e nas exportações. Nada é apagado.</p>
      </ConfirmDialog>
    </>
  );
}

const inputCls = "block w-full rounded-ctl border border-field bg-white px-3 py-2 text-[15px] disabled:bg-locked disabled:text-muted aria-[invalid=true]:border-danger-ink";

function Cell({ label, error, children }: { label: string; error?: string | undefined; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-xs font-bold md:sr-only">{label}</label>
      {children(id)}
      {error && <p role="alert" className="mt-1 text-xs font-medium text-danger-ink">{error}</p>}
    </div>
  );
}

function IconBtn({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}
      className="inline-flex size-9 items-center justify-center rounded-ctl border border-line-strong bg-surface text-ink shadow-e1 transition-[background-color,border-color,transform] duration-[var(--duration-base)] hover:border-ink-3/50 hover:bg-paper active:translate-y-px disabled:opacity-40 disabled:shadow-none">
      {children}
    </button>
  );
}
